import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useMsal } from "@azure/msal-react";
import { getUsernameFromMsal } from "../Services/GraphService";

/**
 * `tokyoNight` is the calm default; `kawaii` is the maximalist one.
 *
 * Nothing is migrated across renames of these values — `safeMode` below drops
 * an unrecognised preference back to the default, which is the entire
 * migration story.
 */
export type ThemeMode = "tokyoNight" | "kawaii";

export const DEFAULT_THEME_MODE: ThemeMode = "tokyoNight";

const getStorageKey = (username: string) => `jepx-lazer-desk-theme-${username}`;

// Cache to store one atom per user
const atomCache = new Map<
  string,
  ReturnType<typeof atomWithStorage<ThemeMode>>
>();

export const getThemeModeAtom = (username: string) => {
  if (!atomCache.has(username)) {
    atomCache.set(
      username,
      atomWithStorage<ThemeMode>(getStorageKey(username), DEFAULT_THEME_MODE)
    );
  }
  return atomCache.get(username)!;
};

export const useThemeMode = (): [ThemeMode, (mode: ThemeMode) => void] => {
  const { accounts, instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const username = getUsernameFromMsal(
    activeAccount ? [activeAccount] : accounts
  );

  const themeModeAtom = getThemeModeAtom(username);
  const [mode, setMode] = useAtom(themeModeAtom);

  // A value persisted under an older naming (`light`, `dark`, `japandi`) would
  // not match either mode; fall back rather than handing an unknown string to
  // the ThemeProvider.
  const safeMode: ThemeMode =
    mode === "tokyoNight" || mode === "kawaii" ? mode : DEFAULT_THEME_MODE;

  return [safeMode, setMode];
};
